import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui';
import { adminStatsService, PendingActionsCount } from '@/services/adminStats.service';

interface PendingActionsProps {
  className?: string;
}

interface ActionItemProps {
  count: number;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: 'warning' | 'danger' | 'info' | 'success';
  link: string;
  isLoading?: boolean;
}

const colorConfig = {
  warning: {
    bg: 'bg-warning-50',
    iconBg: 'bg-warning-100',
    text: 'text-warning-600',
    border: 'border-warning-200',
    countBg: 'bg-warning-500',
  },
  danger: {
    bg: 'bg-danger-50',
    iconBg: 'bg-danger-100',
    text: 'text-danger-600',
    border: 'border-danger-200',
    countBg: 'bg-danger-500',
  },
  info: {
    bg: 'bg-blue-50',
    iconBg: 'bg-blue-100',
    text: 'text-blue-600',
    border: 'border-blue-200',
    countBg: 'bg-blue-500',
  },
  success: {
    bg: 'bg-success-50',
    iconBg: 'bg-success-100',
    text: 'text-success-600',
    border: 'border-success-200',
    countBg: 'bg-success-500',
  },
};

const ActionItem: React.FC<ActionItemProps> = ({
  count,
  label,
  description,
  icon,
  color,
  link,
  isLoading,
}) => {
  const colors = colorConfig[color];

  if (isLoading) {
    return (
      <div className="p-4 rounded-xl border border-secondary-200 animate-pulse">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-secondary-200 rounded-xl" />
          <div className="flex-1">
            <div className="h-4 w-24 bg-secondary-200 rounded mb-2" />
            <div className="h-3 w-32 bg-secondary-200 rounded" />
          </div>
          <div className="h-8 w-8 bg-secondary-200 rounded-full" />
        </div>
      </div>
    );
  }

  const hasActions = count > 0;

  return (
    <Link
      to={link}
      className={`block p-4 rounded-xl border transition-all duration-200 ${
        hasActions
          ? `${colors.border} ${colors.bg} hover:shadow-md`
          : 'border-secondary-200 bg-white hover:bg-secondary-50'
      }`}
    >
      <div className="flex items-start space-x-4">
        <div
          className={`p-3 rounded-xl ${
            hasActions ? colors.iconBg : 'bg-secondary-100'
          }`}
        >
          <div className={hasActions ? colors.text : 'text-secondary-400'}>
            {icon}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm font-semibold ${
              hasActions ? 'text-secondary-900' : 'text-secondary-600'
            }`}
          >
            {label}
          </p>
          <p className="text-xs text-secondary-500 mt-0.5">{description}</p>
        </div>
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
            hasActions ? `${colors.countBg} text-white` : 'bg-secondary-200 text-secondary-500'
          }`}
        >
          {count}
        </div>
      </div>
      {hasActions && (
        <div className="mt-3 flex items-center text-xs font-medium text-secondary-600">
          <span>Review now</span>
          <svg
            className="ml-1 w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      )}
    </Link>
  );
};

export const PendingActions: React.FC<PendingActionsProps> = ({ className = '' }) => {
  const [actions, setActions] = useState<PendingActionsCount | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const data = await adminStatsService.getPendingActions();
        setActions(data);
      } catch (err) {
        setError('Failed to load pending actions');
        console.error('Error fetching pending actions:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalPending = actions
    ? actions.pendingTopups +
      actions.pendingVerifications +
      actions.flaggedActivities +
      actions.expiringSoonSubscriptions
    : 0;

  if (error) {
    return (
      <Card className={className}>
        <Card.Header>
          <h2 className="text-lg font-semibold text-secondary-900">Pending Actions</h2>
        </Card.Header>
        <Card.Body>
          <div className="h-32 flex items-center justify-center">
            <div className="text-center">
              <svg
                className="mx-auto h-10 w-10 text-danger-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="mt-2 text-sm text-secondary-600">{error}</p>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">Pending Actions</h2>
            <p className="text-sm text-secondary-500">Items requiring your attention</p>
          </div>
          {totalPending > 0 && (
            <div className="px-3 py-1 bg-danger-100 text-danger-700 text-sm font-semibold rounded-full">
              {totalPending} total
            </div>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        <div className="space-y-3">
          <ActionItem
            count={actions?.pendingTopups ?? 0}
            label="Pending Topup Requests"
            description="Balance topup requests awaiting approval"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            }
            color="warning"
            link="/topup-requests"
            isLoading={isLoading}
          />

          <ActionItem
            count={actions?.pendingVerifications ?? 0}
            label="Pending Verifications"
            description="User accounts awaiting verification"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            }
            color="info"
            link="/users?status=pending"
            isLoading={isLoading}
          />

          <ActionItem
            count={actions?.flaggedActivities ?? 0}
            label="Flagged Activities"
            description="Suspicious activities requiring review"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
                />
              </svg>
            }
            color="danger"
            link="/audit-logs?flagged=true"
            isLoading={isLoading}
          />

          <ActionItem
            count={actions?.expiringSoonSubscriptions ?? 0}
            label="Expiring Subscriptions"
            description="Subscriptions expiring within 7 days"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
            color="success"
            link="/tenants?expiring=true"
            isLoading={isLoading}
          />
        </div>

        {!isLoading && totalPending === 0 && (
          <div className="mt-4 p-4 bg-success-50 rounded-xl border border-success-200 text-center">
            <svg
              className="mx-auto w-8 h-8 text-success-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="mt-2 text-sm font-medium text-success-700">All caught up!</p>
            <p className="text-xs text-success-600">No pending actions at this time</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default PendingActions;
